import {
  AIStarsIcon,
  AdminDollarInput2,
  Button,
  Disclaimer,
  Div,
  Icon,
  P,
  Separator,
  Tab,
  TextButton,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { useState } from 'react';
import { PerOrderFeeSuggestionModal } from './PerOrderFeeSuggestionModal';
import {
  PER_DAY_FEE_MAX,
  PER_ORDER_FEE_MAX,
  VTabletBillingMode,
  perDayForMonthly,
} from './servicesTotal';
import { usePerOrderFeeSuggestion } from './usePerOrderFeeSuggestion';

type Props = {
  locationId: number | string;
  /**
   * Cents: the monthly fee picked in the form, saved or not, which the
   * suggestion has to add up to. Undefined while none is picked.
   */
  monthlyTarget: number | undefined;
  billing: VTabletBillingMode;
  onBillingChange: (billing: VTabletBillingMode) => void;
  /** Cents; 0 while nothing is entered. */
  perOrderFee: number;
  onPerOrderFeeChange: (fee: number) => void;
  /** Cents; 0 while nothing is entered. */
  perDayFee: number;
  onPerDayFeeChange: (fee: number) => void;
};

const BILLING_OPTIONS: { billing: VTabletBillingMode; label: string }[] = [
  { billing: 'monthly', label: 'Monthly' },
  { billing: 'perOrder', label: 'Per order' },
  { billing: 'perDay', label: 'Per day' },
];

/**
 * How vTablet is billed, under its Service Fee in the services modal: the
 * monthly fee by ACH, an amount withheld from every virtual-brand order
 * payout, or an amount taken from the vGrubs balance every day. Only the
 * vTablet fee moves; extra tablets, printers and Revenue Recovery stay
 * monthly.
 */
export const VTabletBilling = ({
  locationId,
  monthlyTarget,
  billing,
  onBillingChange,
  perOrderFee,
  onPerOrderFeeChange,
  perDayFee,
  onPerDayFeeChange,
}: Props) => {
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const suggestion = usePerOrderFeeSuggestion(locationId);

  const openSuggestion = () => {
    setSuggestionOpen(true);
    suggestion.mutate(monthlyTarget);
  };

  const closeSuggestion = () => {
    setSuggestionOpen(false);
    // So the next opening starts from the loader, not from this answer.
    suggestion.reset();
  };

  return (
    <>
      <Separator $m="16px 0" />
      {/* Three tabs beside the label leave no room on a phone: there they
          go under it. */}
      <Div $flex $alignCenter $between $gap="12px" $columnSM $alignStartSM>
        <P $m="0">Billing</P>
        <Div $flex $gap="8px">
          {BILLING_OPTIONS.map((option) => (
            <Tab
              key={option.label}
              $small
              $minWidthAuto
              $minWidthAutoMD
              $minWidthAutoSM
              selected={billing === option.billing}
              onClick={() => onBillingChange(option.billing)}
            >
              {option.label}
            </Tab>
          ))}
        </Div>
      </Div>

      {billing === 'perDay' && (
        <PerDayFee
          monthlyTarget={monthlyTarget}
          fee={perDayFee}
          onFeeChange={onPerDayFeeChange}
        />
      )}

      {billing === 'perOrder' && (
        <>
          <Separator $m="16px 0" />
          <Div $flex $alignCenter $between $gap="12px">
            <div>
              <P $m="0">Amount per order</P>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                $0.01 to $5.00
              </Disclaimer>
            </div>
            <Div $width="100px" $flexNone>
              <AdminDollarInput2
                value={perOrderFee}
                onValueChange={onPerOrderFeeChange}
                max={PER_ORDER_FEE_MAX}
                $textCenter
              />
            </Div>
          </Div>
          <Button
            $blueGradient
            $selected
            $w="100%"
            $wSM="100%"
            $mt="12"
            onClick={openSuggestion}
          >
            <Div $flex $center $gap="8px">
              <Icon>
                <AIStarsIcon />
              </Icon>
              Suggest with AI
            </Div>
          </Button>
          <Disclaimer $m="12px 0 0" $color={lightTheme.lightGray}>
            Charged on every virtual-brand order payout. Agent vOrders
            commission is not paid on per-order billing.
          </Disclaimer>
        </>
      )}

      <PerOrderFeeSuggestionModal
        isOpen={suggestionOpen}
        closeModal={closeSuggestion}
        isGenerating={suggestion.isPending}
        data={suggestion.data}
        error={suggestion.error}
        onRetry={() => suggestion.mutate(monthlyTarget)}
        onUse={(fee) => {
          onPerOrderFeeChange(fee);
          closeSuggestion();
        }}
      />
    </>
  );
};

/**
 * The amount taken from the vGrubs balance every day, with what a day needs
 * to reach the monthly fee picked above (the most a month can bring).
 */
const PerDayFee = ({
  monthlyTarget,
  fee,
  onFeeChange,
}: {
  monthlyTarget: number | undefined;
  fee: number;
  onFeeChange: (fee: number) => void;
}) => {
  // A $0 plan has no monthly cap, so there is nothing to work a day out of.
  const suggested =
    monthlyTarget != null && monthlyTarget > 0
      ? Math.min(perDayForMonthly(monthlyTarget), PER_DAY_FEE_MAX)
      : null;

  return (
    <>
      <Separator $m="16px 0" />
      <Div $flex $alignCenter $between $gap="12px">
        <div>
          <P $m="0">Amount per day</P>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            $0.01 to $5.00
          </Disclaimer>
        </div>
        <Div $width="100px" $flexNone>
          <AdminDollarInput2
            value={fee}
            onValueChange={onFeeChange}
            max={PER_DAY_FEE_MAX}
            $textCenter
          />
        </Div>
      </Div>
      {suggested != null && monthlyTarget != null && (
        <Div $flex $alignCenter $between $gap="12px" $mt="12">
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {displayCentsInDollars(suggested)} a day reaches the{' '}
            {displayCentsInDollars(monthlyTarget)} monthly fee in 30 days.
          </Disclaimer>
          {fee !== suggested && (
            <TextButton
              color={lightTheme.primary}
              $underline
              style={{ width: 'auto', height: 'auto', flex: 'none' }}
              onClick={() => onFeeChange(suggested)}
            >
              Use {displayCentsInDollars(suggested)}
            </TextButton>
          )}
        </Div>
      )}
      <Disclaimer $m="12px 0 0" $color={lightTheme.lightGray}>
        Taken from the vGrubs balance (vPay Card) every day at 11:00 New York,
        even into the negative.{' '}
        {monthlyTarget
          ? `Never more than ${displayCentsInDollars(
              monthlyTarget
            )} in a month; missed days are not made up.`
          : 'Missed days are not made up.'}{' '}
        Agent vOrders commission is not paid on per-day billing.
      </Disclaimer>
    </>
  );
};
